import {
  Box,
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { DocAccessSubject, DocAccessSubjectType } from "@xop/dashboard-shared";

import { Pagination } from "@/components/ui/pagination";
import { listAccessSubjects } from "../api.js";

/**
 * The account rail: pick whose view of the library you are editing.
 *
 * Customers and resellers are two tabs rather than one merged list because
 * they are not interchangeable — a customer bought products, a reseller
 * sells them, and the folders published to each portal differ. Merging them
 * would produce a list where the same search term means two things.
 *
 * Each row shows the account's products, because "which documents should
 * this account see" is almost always answered by "what did they buy", and
 * having to open the account to find out defeats the rail.
 */
export function SubjectRail({
  type,
  onTypeChange,
  selectedId,
  onSelect,
}: {
  type: DocAccessSubjectType;
  onTypeChange: (next: DocAccessSubjectType) => void;
  selectedId: number | null;
  onSelect: (subject: DocAccessSubject) => void;
}): JSX.Element {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useQuery({
    queryKey: ["docs", "access", "subjects", type, debounced, page],
    queryFn: () =>
      listAccessSubjects({ type, page, per_page: 25, ...(debounced ? { q: debounced } : {}) }),
  });

  const subjects = query.data?.data ?? [];
  const meta = query.data?.meta;

  return (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <Tabs
        value={type}
        onChange={(_, next: DocAccessSubjectType) => {
          onTypeChange(next);
          setPage(1);
        }}
        variant="fullWidth"
        sx={{ "& .MuiTab-root": { fontSize: 13 } }}
      >
        <Tab label="Customers" value="organization" data-testid="access-tab-customers" />
        <Tab label="Resellers" value="reseller" data-testid="access-tab-resellers" />
      </Tabs>

      <TextField
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={type === "organization" ? "Search customers" : "Search resellers"}
        inputProps={{ "data-testid": "access-subject-search" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {query.isLoading ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
          Loading accounts…
        </Typography>
      ) : subjects.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
          {type === "reseller"
            ? "No reseller companies have synced from the CRM yet."
            : "No customers match that search."}
        </Typography>
      ) : (
        <List dense disablePadding data-testid="access-subject-list">
          {subjects.map((subject) => (
            <SubjectRow
              key={subject.id}
              subject={subject}
              selected={subject.id === selectedId}
              onSelect={() => onSelect(subject)}
            />
          ))}
        </List>
      )}

      {meta ? (
        <Pagination
          page={page}
          perPage={meta.per_page}
          totalCount={meta.total_count}
          totalPages={meta.total_pages}
          onPageChange={setPage}
          entityNoun={type === "organization" ? "customers" : "resellers"}
          hideIfSinglePage
        />
      ) : null}
    </Stack>
  );
}

function SubjectRow({
  subject,
  selected,
  onSelect,
}: {
  subject: DocAccessSubject;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const shown = subject.products.slice(0, 2);
  const extra = subject.products.length - shown.length;

  return (
    <ListItemButton
      selected={selected}
      onClick={onSelect}
      data-testid={`access-subject-${subject.id}`}
      sx={{
        borderRadius: 1,
        mb: 0.5,
        alignItems: "flex-start",
        flexDirection: "column",
        gap: 0.5,
        "&.Mui-selected": { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) },
      }}
    >
      <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, minWidth: 0 }} noWrap>
          {subject.name}
        </Typography>
        {subject.doc_access_mode === "strict" ? (
          <Chip size="small" label="Whitelist" color="warning" sx={{ height: 18, fontSize: 11 }} />
        ) : null}
        {subject.rules_count > 0 ? (
          <Chip
            size="small"
            label={`${subject.rules_count} override${subject.rules_count === 1 ? "" : "s"}`}
            variant="outlined"
            sx={{ height: 18, fontSize: 11 }}
          />
        ) : null}
      </Box>

      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {shown.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No products
          </Typography>
        ) : (
          shown.map((product) => (
            <Chip
              key={product.code}
              size="small"
              label={product.name}
              variant="outlined"
              sx={{ height: 18, fontSize: 11 }}
            />
          ))
        )}
        {extra > 0 ? (
          <Chip size="small" label={`+${extra}`} variant="outlined" sx={{ height: 18, fontSize: 11 }} />
        ) : null}
      </Box>
    </ListItemButton>
  );
}
